/**
 * 模型数据管理 Store
 *
 * 使用 zustand 缓存 LLM 模型数据，避免重复API请求。
 * 提供模型列表、模型详细信息的统一管理和访问。
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

// ==================== 类型定义 ====================

/**
 * 模型详细信息
 */
export interface ModelInfo {
  id: string;
  name: string;
  provider?: string;
  max_tokens?: number;
  description?: string;
  supports_thinking?: boolean;
  supported_params?: string[];
  defaults?: {
    temperature?: number;
    max_tokens?: number;
    top_p?: number;
    top_k?: number;
    thinking?: boolean;
  };
  context_length?: number; // 用于 ChatInput 组件显示上下文长度
}

/**
 * 模型状态接口
 */
interface ModelState {
  // ===== 数据 =====
  /** 所有可用的模型ID列表（来自 capabilities） */
  allModelIds: string[];

  /** 经过健康检查的可用模型ID列表（来自 /available 端点） */
  availableModelIds: string[];

  /** 模型详细信息映射表 */
  modelsInfo: Record<string, ModelInfo>;

  /** 最后验证时间 */
  lastValidated: string | null;

  // ===== 加载状态 =====
  isLoading: boolean;
  isFetching: boolean; // 正在请求中（用于防止并发请求）
  error: string | null;

  // ===== 方法 =====
  /** 获取所有模型数据（包括可用列表和详细信息） */
  fetchModels: () => Promise<void>;

  /** 获取模型详细信息 */
  getModelInfo: (modelId: string) => ModelInfo | undefined;

  /** 清除错误 */
  clearError: () => void;
}

// ==================== 创建 Store ====================

export const useModelStore = create<ModelState>()(
  immer((set, get) => ({
    // ===== 初始状态 =====
    allModelIds: [],
    availableModelIds: [],
    modelsInfo: {},
    lastValidated: null,
    isLoading: false,
    isFetching: false,
    error: null,

    // ===== 方法实现 =====

    /**
     * 获取所有模型数据
     * 1. 首先尝试获取经过健康检查的可用模型列表
     * 2. 然后获取模型详细能力配置
     *
     * 使用 isFetching 标志防止并发请求
     */
    fetchModels: async () => {
      const state = get();

      // 如果已经有数据且没有错误，跳过请求
      if (state.availableModelIds.length > 0 && Object.keys(state.modelsInfo).length > 0 && !state.error) {
        return;
      }

      // 如果正在请求中，直接返回（防止并发请求）
      if (state.isFetching) {
        return;
      }

      set((state) => {
        state.isLoading = true;
        state.isFetching = true;
        state.error = null;
      });

      try {
        // 1. 尝试获取可用模型列表（经过健康检查）
        const availableResponse = await fetch('/api/settings/models/available');

        if (availableResponse.ok) {
          const availableData = await availableResponse.json();

          if (availableData.models && Array.isArray(availableData.models)) {
            set((state) => {
              state.availableModelIds = availableData.models;
              state.lastValidated = availableData.last_validated;
            });
          }
        }

        // 2. 获取模型详细能力配置
        const capabilitiesResponse = await fetch('/api/settings/models/capabilities');

        if (!capabilitiesResponse.ok) {
          throw new Error('获取模型配置失败');
        }

        const capabilitiesData = await capabilitiesResponse.json();

        if (capabilitiesData.models && typeof capabilitiesData.models === 'object') {
          const models: string[] = [];
          const infoMap: Record<string, ModelInfo> = {};

          Object.entries(capabilitiesData.models).forEach(([id, config]: [string, any]) => {
            models.push(id);
            infoMap[id] = {
              id,
              name: config.name,
              provider: config.provider,
              max_tokens: config.max_tokens,
              description: config.description,
              supports_thinking: config.supports_thinking,
              supported_params: config.supported_params,
              defaults: config.defaults,
              // 兼容 ChatInput 需要的字段
              context_length: config.context_length,
            };
          });

          set((state) => {
            state.allModelIds = models;
            state.modelsInfo = infoMap;
            state.isLoading = false;
            state.isFetching = false;
          });
        } else {
          throw new Error('无效的模型数据格式');
        }
      } catch (err) {
        console.error('[ModelStore] 获取模型数据失败:', err);
        set((state) => {
          state.error = err instanceof Error ? err.message : '获取模型数据失败';
          state.isLoading = false;
          state.isFetching = false;
        });
      }
    },

    /**
     * 获取模型详细信息
     */
    getModelInfo: (modelId: string) => {
      return get().modelsInfo[modelId];
    },

    /**
     * 清除错误
     */
    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },
  }))
);

export default useModelStore;
